/**
 * The right-hand panel for one node: what it is, what it needs, what it
 * produced.
 *
 * Inputs are rendered from the node's spec — and for an app action, from the
 * action's manifest — as mappable fields: type a value, pick one from an
 * earlier step with `{x}`, or choose from a live dropdown when the app offers
 * one. "Test this step" runs the workflow up to here and shows the items,
 * which then feed the picker for the nodes after it.
 */

import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { actionsApi } from '@/core/api/actions.api'
import { useConnections } from '@/core/connection/connection.context'
import type { JsonSchema, LookupSpec } from '@/core/types/api.types'
import { queryKeys } from '@/lib/query.client'
import { Logo, relativeTime } from '@/shared/components'
import { useActionSchema, useWorkflows } from '../api'
import { useGraphModel, useGraphs } from '@/features/brain/hooks'
import { NodeIcon } from '../canvas/icons'
import type { AppSpec, NodeSpec, ParamSpec, StepRecord, WfNode, Workflow } from '../types'
import { CodeInput, Field, JsonInput, MapInput, MappableInput, RowsInput, asText, type MapContext } from './inputs'
import { isInsideLoop, useSources } from './useSources'
import { ContractTable, WebhookPreview } from './WebhookContract'

interface Props {
  node: WfNode
  spec: NodeSpec | undefined
  draft: Workflow
  specs: Record<string, NodeSpec>
  apps: AppSpec[]
  step?: StepRecord
  steps: Record<string, StepRecord>
  testing: boolean
  onChange: (patch: Partial<WfNode>) => void
  onDelete: () => void
  onTest: () => void
}

export function NodeInspector({ node, spec, draft, specs, apps, step, steps, testing, onChange, onDelete, onTest }: Props) {
  const [tab, setTab] = useState<'config' | 'output'>('config')
  useEffect(() => setTab('config'), [node.id])
  const { byId: connectionsById } = useConnections()

  const insideLoop = useMemo(() => isInsideLoop(node.id, draft.nodes, draft.edges, specs), [node.id, draft.nodes, draft.edges, specs])
  const sources = useSources(node, draft.nodes, draft.edges, specs, steps, draft.variables, insideLoop)
  const map: MapContext = useMemo(() => ({ sources, specs }), [sources, specs])
  // A trigger's own settings run before anything exists to map from.
  const isTrigger = node.type.startsWith('trigger.')

  const setParam = (key: string, value: unknown) => onChange({ params: { ...node.params, [key]: value } })
  const visible = (p: ParamSpec) =>
    !p.when || Object.entries(p.when).every(([k, vals]) => vals.includes(node.params[k] ?? spec?.params.find((x) => x.key === k)?.default))

  const isApp = node.type === 'connector.action' || node.type === 'trigger.app' || node.type === 'connector.request'
  const connection = connectionsById[String(node.params.connection_id ?? '')]
  const connectorId = String(node.params.connector_id ?? connection?.connector_id ?? '')
  const app = apps.find((a) => a.id === connectorId)
  const action = app?.actions.find((a) => a.id === String(node.params.action_id ?? ''))
  const outputCount = step ? Object.values(step.output_counts).reduce((a, b) => a + b, 0) : 0
  const hasIncoming = draft.edges.some((e) => e.target === node.id)

  return (
    <div className="wf-inspector">
      <div className="wf-inspector-head">
        {app ? (
          <Logo src={app.logo} name={app.name} id={app.id} />
        ) : (
          <span className="wf-node-icon wf-node-icon-lg" data-group={spec?.group}>
            <NodeIcon name={spec?.icon ?? 'workflow'} />
          </span>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <input className="wf-inspector-name" value={node.name} aria-label="Node name" onChange={(e) => onChange({ name: e.target.value })} />
          <div className="wf-inspector-type">
            {app ? `${app.name} · ${action?.name ?? spec?.label ?? node.type}` : spec?.label ?? node.type}
          </div>
        </div>
      </div>

      <div className="wf-tabs" role="tablist">
        <button type="button" role="tab" aria-selected={tab === 'config'} data-on={tab === 'config'} onClick={() => setTab('config')}>Configure</button>
        <button type="button" role="tab" aria-selected={tab === 'output'} data-on={tab === 'output'} onClick={() => setTab('output')}>
          Output {step && <span className="wf-count" data-status={step.status}>{step.status === 'error' ? '!' : outputCount}</span>}
        </button>
        <span className="spacer" />
        <button type="button" className="btn btn-sm wf-teststep" onClick={onTest} disabled={testing} title="Run the workflow up to this node">
          {testing ? 'Testing…' : 'Test this step'}
        </button>
      </div>

      {tab === 'config' ? (
        <div className="wf-params">
          {spec?.summary && !isApp && <p className="wf-inspector-summary">{spec.summary}</p>}
          {!spec?.has_input ? null : !hasIncoming ? (
            <p className="wf-note wf-note-warn">Nothing is wired into this step yet. Use the <strong>+</strong> on an earlier node, or drag from its output dot.</p>
          ) : null}

          {node.type === 'trigger.webhook' && <WebhookPreview node={node} workflowActive={draft.active} />}
          {(node.type === 'trigger.manual' || node.type === 'trigger.callable') && (
            <div className="wf-hint">
              <div className="small muted">{node.type === 'trigger.callable' ? 'Other workflows call this with “Call workflow”. From outside:' : 'Start it from anywhere:'}</div>
              <code className="mono wf-url">POST /api/v1/workflows/{draft.workflow_id}/run {'{"input": …}'}</code>
            </div>
          )}
          {node.type === 'trigger.app' && <AppEventStatus draft={draft} />}

          {isApp && (
            <AppSection node={node} app={app} apps={apps} connectorId={connectorId} setParam={setParam} onChange={onChange} />
          )}

          {(spec?.params ?? []).filter(visible).filter((p) => !(isApp && (p.kind === 'connection' || p.kind === 'action'))).map((p) => (
            <ParamField key={p.key} param={p} node={node} value={node.params[p.key] ?? p.default} connectorId={connectorId} map={isTrigger ? null : map} onChange={(v) => setParam(p.key, v)} />
          ))}
          {(spec?.params ?? []).length === 0 && <p className="muted small">This step has nothing to configure.</p>}

          <details className="wf-details">
            <summary>Behaviour</summary>
            <div className="wf-details-body">
              <Field label="If this step fails">
                <select className="select" value={node.on_error ?? 'fail'} onChange={(e) => onChange({ on_error: e.target.value as WfNode['on_error'] })}>
                  <option value="fail">Stop the run and mark it failed</option>
                  <option value="continue">Log it and continue with the same items</option>
                </select>
              </Field>
              <Field label="Retry before failing">
                <select className="select" value={String(node.retries ?? 0)} onChange={(e) => onChange({ retries: Number(e.target.value) })}>
                  {[0, 1, 2, 3, 5].map((n) => (
                    <option key={n} value={n}>{n === 0 ? 'Never' : `${n} time${n === 1 ? '' : 's'}`}</option>
                  ))}
                </select>
              </Field>
              <label className="checkbox-row">
                <input type="checkbox" checked={Boolean(node.disabled)} onChange={(e) => onChange({ disabled: e.target.checked })} />
                <span className="small">Disabled — items pass straight through</span>
              </label>
              <Field label="Notes">
                <textarea className="textarea" rows={2} value={node.notes ?? ''} onChange={(e) => onChange({ notes: e.target.value })} placeholder="Why this step is here" />
              </Field>
            </div>
          </details>

          <button type="button" className="btn btn-ghost btn-sm wf-danger" onClick={onDelete} style={{ alignSelf: 'flex-start' }}>
            Remove step
          </button>
        </div>
      ) : (
        <StepOutput step={step} onTest={onTest} testing={testing} />
      )}
    </div>
  )
}

// --- the app section: which account, which action ------------------------------------------------

function AppSection({ node, app, apps, connectorId, setParam, onChange }: { node: WfNode; app: AppSpec | undefined; apps: AppSpec[]; connectorId: string; setParam: (k: string, v: unknown) => void; onChange: (p: Partial<WfNode>) => void }) {
  const { connections, byConnector } = useConnections()
  const list = connectorId ? byConnector[connectorId] ?? [] : connections
  const connectionId = String(node.params.connection_id ?? '')
  const isEvent = node.type === 'trigger.app'
  return (
    <div className="wf-appsection">
      {!connectorId && (
        <Field label="App" required>
          <select
            className="select"
            value=""
            onChange={(e) => {
              const a = apps.find((x) => x.id === e.target.value)
              if (!a) return
              const only = byConnector[a.id]?.length === 1 ? byConnector[a.id][0].connection_id : ''
              onChange({ params: { ...node.params, connector_id: a.id, connection_id: only, action_id: '', input: {} }, name: node.name })
            }}
          >
            <option value="">Choose an app</option>
            {apps.map((a) => (
              <option key={a.id} value={a.id}>{a.name}{(byConnector[a.id]?.length ?? 0) ? '' : ' (not connected)'}</option>
            ))}
          </select>
        </Field>
      )}
      <Field label="Account" required help={list.length === 0 && app ? <Link to={`/connectors/${encodeURIComponent(app.id)}`}>Connect {app.name} →</Link> : undefined}>
        <select className="select" value={connectionId} onChange={(e) => setParam('connection_id', e.target.value)}>
          <option value="">{list.length ? 'Choose an account' : app ? `${app.name} is not connected` : 'Choose a connection'}</option>
          {list.map((c) => (
            <option key={c.connection_id} value={c.connection_id}>
              {c.label}{!connectorId ? ` (${c.connector_name ?? c.connector_id})` : ''}{c.status !== 'active' ? ` — ${c.status}` : ''}
            </option>
          ))}
        </select>
      </Field>
      {node.type !== 'connector.request' && app && (
        <Field label={isEvent ? 'Watch' : 'Action'} required help={isEvent ? 'A list action. Each check, items with an unseen key start a run.' : undefined}>
          <select
            className="select"
            value={String(node.params.action_id ?? '')}
            onChange={(e) => {
              const a = app.actions.find((x) => x.id === e.target.value)
              onChange({ params: { ...node.params, action_id: e.target.value, input: {} }, name: a ? (isEvent ? `New ${a.name.toLowerCase().replace(/^(list|search|get)\s+/i, '')}` : `${app.name}: ${a.name}`) : node.name })
            }}
          >
            <option value="">Choose an action</option>
            {groupActions(app.actions.filter((a) => !isEvent || a.watchable)).map(([g, items]) => (
              <optgroup key={g} label={g}>
                {items.map((a) => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </optgroup>
            ))}
          </select>
        </Field>
      )}
    </div>
  )
}

function groupActions<T extends { category: string | null }>(actions: T[]): [string, T[]][] {
  const groups = new Map<string, T[]>()
  for (const a of actions) groups.set(a.category ?? 'Actions', [...(groups.get(a.category ?? 'Actions') ?? []), a])
  return [...groups.entries()]
}

// --- one parameter -------------------------------------------------------------------------------

function ParamField({ param, node, value, connectorId, map, onChange }: { param: ParamSpec; node: WfNode; value: unknown; connectorId: string; map: MapContext | null; onChange: (v: unknown) => void }) {
  switch (param.kind) {
    case 'text':
    case 'expression':
      return (
        <Field label={param.label} required={param.required} help={param.help}>
          <MappableInput value={value} placeholder={param.placeholder} onChange={onChange} map={map} />
        </Field>
      )
    case 'number':
      return (
        <Field label={param.label} required={param.required} help={param.help}>
          <MappableInput value={value} placeholder={param.placeholder} onChange={(v) => onChange(coerceNumber(v))} map={map} />
        </Field>
      )
    case 'boolean':
      return (
        <label className="checkbox-row">
          <input type="checkbox" checked={Boolean(value)} onChange={(e) => onChange(e.target.checked)} />
          <span>
            <span className="small" style={{ fontWeight: 600 }}>{param.label}</span>
            {param.help && <span className="field-help" style={{ display: 'block' }}>{param.help}</span>}
          </span>
        </label>
      )
    case 'select':
      return (
        <Field label={param.label} required={param.required} help={param.help}>
          <select className="select" value={String(value ?? '')} onChange={(e) => onChange(e.target.value)}>
            {param.options.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </Field>
      )
    case 'json':
      return (
        <Field label={param.label} required={param.required} help={param.help}>
          <JsonInput value={value} placeholder={param.placeholder} onChange={onChange} map={map} />
        </Field>
      )
    case 'code':
      return (
        <Field
          label={param.label}
          required={param.required}
          help={
            <>
              {param.help} Names: <code>$json</code>, <code>$items</code>, <code>$node("Name")</code>, <code>$input</code>, <code>$vars</code>, <code>$loop</code>. Functions: <code>get</code>, <code>pluck</code>, <code>group_by</code>, <code>sort_by</code>, <code>now</code>, <code>date_add</code>, <code>to_json</code>…
            </>
          }
        >
          <CodeInput value={String(value ?? '')} onChange={onChange} map={map ?? { sources: [], specs: {} }} />
        </Field>
      )
    case 'map':
      return (
        <Field label={param.label} required={param.required} help={param.help}>
          <MapInput value={(value as Record<string, unknown>) ?? {}} onChange={onChange} map={map} />
        </Field>
      )
    case 'fields':
      return (
        <Field label={param.label} required={param.required} help={param.help}>
          <RowsInput
            rows={(value as Record<string, unknown>[]) ?? []}
            keyPlaceholder={param.placeholder || 'field'}
            valuePlaceholder={node.type === 'trigger.callable' ? 'example value' : 'value or {{ $json.x }}'}
            onChange={onChange}
            addLabel={node.type === 'trigger.callable' ? 'Add input' : 'Add field'}
            map={map}
          />
        </Field>
      )
    case 'cases': {
      const byValue = node.params.mode === 'value'
      return (
        <Field label={param.label} required={param.required} help="Each case becomes an output port. The first matching case wins; anything else leaves on default.">
          <RowsInput
            rows={(value as Record<string, unknown>[]) ?? []}
            keyPlaceholder="port name"
            valuePlaceholder={byValue ? 'equals…' : '$json.kind == "x"'}
            valueKey={byValue ? 'match' : 'condition'}
            keyKey="name"
            onChange={onChange}
            addLabel="Add case"
            map={map}
          />
        </Field>
      )
    }
    case 'contract':
      return (
        <Field label={param.label} required={param.required} help={param.help}>
          <ContractTable value={value} kind={param.key === 'headers' ? 'headers' : 'query'} onChange={onChange} />
        </Field>
      )
    case 'action_input':
      return <ActionInputs node={node} connectorId={connectorId} value={(value as Record<string, unknown>) ?? {}} onChange={onChange} map={map} />
    case 'graph':
      return (
        <Field label={param.label} required={param.required} help={param.help}>
          <GraphPicker value={String(value ?? '')} onChange={onChange} />
        </Field>
      )
    case 'node_type':
    case 'edge_type':
      return (
        <Field label={param.label} required={param.required} help={param.help}>
          <ModelKeyPicker kind={param.kind} graphId={String(node.params.graph_id ?? '')} value={value} onChange={onChange} map={map} />
        </Field>
      )
    case 'workflow':
      return (
        <Field label={param.label} required={param.required} help={param.help}>
          <WorkflowPicker value={String(value ?? '')} onChange={onChange} />
        </Field>
      )
    case 'connection':
      return (
        <Field label={param.label} required={param.required} help={param.help}>
          <AnyConnectionPicker value={String(value ?? '')} onChange={onChange} />
        </Field>
      )
    case 'action':
      // Only meaningful with an app section, which renders it itself.
      return null
    default:
      return null
  }
}

function coerceNumber(v: unknown): unknown {
  if (typeof v === 'string' && v.trim() !== '' && !v.includes('{{') && !Number.isNaN(Number(v))) return Number(v)
  return v
}

// --- an action's inputs, from its manifest -----------------------------------------------------------

function ActionInputs({ node, connectorId, value, onChange, map }: { node: WfNode; connectorId: string; value: Record<string, unknown>; onChange: (v: Record<string, unknown>) => void; map: MapContext | null }) {
  const actionId = String(node.params.action_id ?? '')
  const connectionId = String(node.params.connection_id ?? '')
  const schema = useActionSchema(connectorId || undefined, actionId || undefined)
  if (!connectorId || !actionId) return null
  if (schema.isLoading) return <p className="field-help">Loading the action's inputs…</p>
  const props = schema.data?.input_schema?.properties ?? {}
  const required = new Set(schema.data?.input_schema?.required ?? [])
  const keys = Object.keys(props).sort((a, b) => {
    const ra = required.has(a) ? 0 : 1
    const rb = required.has(b) ? 0 : 1
    if (ra !== rb) return ra - rb
    return (props[a]['x-ui']?.order ?? 99) - (props[b]['x-ui']?.order ?? 99)
  })
  if (!keys.length) return <p className="wf-note">This action takes no inputs.</p>
  const set = (k: string, v: unknown) => {
    const next = { ...value }
    if (v === '' || v === undefined || v === null) delete next[k]
    else next[k] = v
    onChange(next)
  }
  const labels = Object.fromEntries(keys.map((k) => [k, props[k]['x-ui']?.label ?? k]))
  return (
    <div className="wf-action-inputs">
      <div className="wf-action-inputs-head">
        <span className="field-label">Inputs</span>
        <span className="small muted">{schema.data?.description}</span>
      </div>
      {keys.map((k) => {
        const s: JsonSchema = props[k]
        const ui = s['x-ui'] ?? {}
        const label = ui.label ?? s.title ?? k
        const help = ui.help ?? s.description
        const lookup = s['x-lookup'] ?? schema.data?.lookups?.[k]
        const current = value[k]
        const isExpr = typeof current === 'string' && current.includes('{{')
        if (lookup && connectionId && !isExpr) {
          return (
            <Field key={k} label={label} required={required.has(k)} help={help}>
              <LookupField connectionId={connectionId} lookup={lookup} value={current} formValues={value} siblingLabels={labels} onChange={(v) => set(k, v)} map={map} />
            </Field>
          )
        }
        if (s.enum && !isExpr) {
          return (
            <Field key={k} label={label} required={required.has(k)} help={help}>
              <div className="wf-map">
                <select className="select" value={asText(current)} onChange={(e) => set(k, e.target.value)}>
                  <option value="">—</option>
                  {s.enum.map((o) => (
                    <option key={String(o)} value={String(o)}>{String(o)}</option>
                  ))}
                </select>
                {map && <button type="button" className="wf-map-btn" title="Use an expression instead" aria-label="Use an expression instead" onClick={() => set(k, '{{ $json. }}')}>{'{x}'}</button>}
              </div>
            </Field>
          )
        }
        if (s.type === 'boolean' && !isExpr) {
          return (
            <label key={k} className="checkbox-row">
              <input type="checkbox" checked={Boolean(current)} onChange={(e) => set(k, e.target.checked)} />
              <span>
                <span className="small" style={{ fontWeight: 600 }}>{label}</span>
                {help && <span className="field-help" style={{ display: 'block' }}>{help}</span>}
              </span>
            </label>
          )
        }
        const multiline = Boolean(ui.multiline) || ui.widget === 'json' || ui.widget === 'textarea' || s.type === 'object' || s.type === 'array'
        return (
          <Field key={k} label={label} required={required.has(k)} help={help}>
            <MappableInput
              value={current}
              placeholder={ui.placeholder || (s.type === 'object' || s.type === 'array' ? '{ … } or {{ $json.x }}' : `${s.type ?? 'value'} or {{ $json.x }}`)}
              onChange={(v) => set(k, s.type === 'number' || s.type === 'integer' ? coerceNumber(v) : v)}
              map={map}
              multiline={multiline}
              mono={ui.widget === 'json'}
            />
          </Field>
        )
      })}
    </div>
  )
}

/** A dropdown filled by the app, with a switch to an expression. */
function LookupField({ connectionId, lookup, value, formValues, siblingLabels, onChange, map }: { connectionId: string; lookup: LookupSpec; value: unknown; formValues: Record<string, unknown>; siblingLabels: Record<string, string>; onChange: (v: unknown) => void; map: MapContext | null }) {
  const dependsOn = Array.isArray(lookup.depends_on) ? lookup.depends_on : []
  const deps: Record<string, unknown> = {}
  for (const k of dependsOn) if (formValues[k] !== undefined && formValues[k] !== '') deps[k] = formValues[k]
  const missing = dependsOn.filter((k) => deps[k] === undefined)
  const [expr, setExpr] = useState(false)
  const q = useQuery({
    queryKey: queryKeys.lookup(connectionId, lookup.action, deps),
    queryFn: ({ signal }) => actionsApi.lookup(connectionId, lookup.action, { labelKey: lookup.label_key || 'name', valueKey: lookup.value_key || 'id', dependencies: deps, signal }),
    enabled: !expr && missing.length === 0,
    staleTime: 120_000,
    retry: false,
  })
  if (expr) return <MappableInput value={value} onChange={onChange} map={map} placeholder="{{ $json.id }}" />
  const options = q.data?.options ?? []
  const known = options.some((o) => String(o.value) === asText(value))
  return (
    <div className="wf-map">
      <select className="select" value={asText(value)} onChange={(e) => onChange(e.target.value)} disabled={missing.length > 0}>
        <option value="">
          {missing.length ? `Choose ${missing.map((k) => siblingLabels[k] ?? k).join(', ')} first` : q.isLoading ? 'Loading…' : q.error ? 'Could not load options' : 'Choose…'}
        </option>
        {!known && asText(value) && <option value={asText(value)}>{asText(value)}</option>}
        {options.map((o) => (
          <option key={String(o.value)} value={String(o.value)}>{o.label}</option>
        ))}
      </select>
      {map && <button type="button" className="wf-map-btn" title="Map a value from an earlier step instead" aria-label="Map a value instead" onClick={() => setExpr(true)}>{'{x}'}</button>}
    </div>
  )
}

function AnyConnectionPicker({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const { connections } = useConnections()
  return (
    <select className="select" value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">{connections.length ? 'Choose a connection' : 'No connections yet'}</option>
      {connections.map((c) => (
        <option key={c.connection_id} value={c.connection_id}>
          {c.label} ({c.connector_name ?? c.connector_id}){c.status !== 'active' ? ` — ${c.status}` : ''}
        </option>
      ))}
    </select>
  )
}

function GraphPicker({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const graphs = useGraphs()
  const list = graphs.data ?? []
  return (
    <select className="select" value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">{graphs.isLoading ? 'Loading graphs…' : list.length ? 'Choose a graph' : 'No graphs yet — create one under Knowledge graph'}</option>
      {list.map((g) => (
        <option key={g.graph_id} value={g.graph_id}>{g.name}{g.status && g.status !== 'draft' ? ` · ${g.status}` : ''}</option>
      ))}
    </select>
  )
}

/** A node or edge type of the chosen graph — from the model, or typed/mapped
    when the type is created by an earlier step and does not exist yet. */
function ModelKeyPicker({ kind, graphId, value, onChange, map }: { kind: 'node_type' | 'edge_type'; graphId: string; value: unknown; onChange: (v: unknown) => void; map: MapContext | null }) {
  const model = useGraphModel(graphId || undefined)
  const [free, setFree] = useState(false)
  const text = asText(value)
  const options = kind === 'node_type' ? (model.data?.node_types ?? []).map((t) => ({ key: t.key, label: `${t.display_name} (${t.key})` })) : (model.data?.edge_types ?? []).map((t) => ({ key: t.key, label: `${t.display_name || t.key} · ${t.from_node_type} → ${t.to_node_type}` }))
  const known = options.some((o) => o.key === text)
  if (!graphId) return <p className="field-help">Choose a graph first.</p>
  if (free || text.includes('{{') || (text && !known && !model.isLoading)) {
    return (
      <div className="wf-modelkey">
        <MappableInput value={value} onChange={onChange} map={map} placeholder={kind === 'node_type' ? 'Person, or {{ $json.type }}' : 'WORKS_AT'} />
        {options.length > 0 && (
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setFree(false); onChange('') }}>Pick from the model</button>
        )}
      </div>
    )
  }
  return (
    <div className="wf-modelkey">
      <select className="select" value={text} onChange={(e) => onChange(e.target.value)}>
        <option value="">{model.isLoading ? 'Loading the model…' : options.length ? `Choose a ${kind === 'node_type' ? 'type' : 'relationship'}` : 'Nothing in the model yet'}</option>
        {options.map((o) => (
          <option key={o.key} value={o.key}>{o.label}</option>
        ))}
      </select>
      <button type="button" className="btn btn-ghost btn-sm" onClick={() => setFree(true)}>Type a key or map one</button>
    </div>
  )
}

function WorkflowPicker({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const list = useWorkflows()
  return (
    <select className="select" value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">Choose a workflow</option>
      {(list.data?.workflows ?? []).map((w) => (
        <option key={w.workflow_id} value={w.workflow_id}>
          {w.name}{w.trigger === 'trigger.callable' ? ' (callable)' : ''}
        </option>
      ))}
    </select>
  )
}

// --- trigger hints -----------------------------------------------------------------------------------

function AppEventStatus({ draft }: { draft: Workflow }) {
  const st = draft.trigger_state?.app
  if (!draft.active) return <p className="wf-note">Activate the workflow to start watching. It checks on the interval below and runs once per batch of new items.</p>
  return (
    <div className="wf-hint">
      <div className="small">
        {st?.last_polled_at ? `Last checked ${relativeTime(st.last_polled_at)}` : 'Not checked yet'}
        {st?.next_run_at ? ` · next ${relativeTime(st.next_run_at)}` : ''}
        {st?.seen_count != null ? ` · ${st.seen_count} known` : ''}
      </div>
      {st?.last_error && <div className="field-error">{st.last_error}</div>}
    </div>
  )
}

// --- output ---------------------------------------------------------------------------------------------

export function StepOutput({ step, onTest, testing }: { step?: StepRecord; onTest?: () => void; testing?: boolean }) {
  const [port, setPort] = useState<string | null>(null)
  if (!step)
    return (
      <div className="wf-params">
        <div className="wf-empty-output">
          <strong>No output yet</strong>
          <p className="muted small">Test this step to run the workflow up to here. Its items then show up in the data picker of every step after it.</p>
          {onTest && <button type="button" className="btn btn-primary btn-sm" onClick={onTest} disabled={testing}>{testing ? 'Testing…' : 'Test this step'}</button>}
        </div>
      </div>
    )
  const ports = Object.keys(step.output)
  const active = port && ports.includes(port) ? port : ports[0]
  return (
    <div className="wf-params">
      <div className="row-tight small muted">
        <span className={`badge ${step.status === 'success' ? 'badge-ok' : 'badge-danger'}`}>{step.status}</span>
        <span>{step.duration_ms} ms</span>
        <span>{step.input_count} in</span>
        {step.iteration != null && <span>iteration {step.iteration}</span>}
      </div>
      {step.error && <p className="field-error">{step.error}</p>}
      {ports.length > 1 && (
        <div className="wf-tabs wf-tabs-inline">
          {ports.map((p) => (
            <button key={p} type="button" data-on={p === active} onClick={() => setPort(p)}>
              {p} <span className="wf-count">{step.output_counts[p] ?? 0}</span>
            </button>
          ))}
        </div>
      )}
      {active && <pre className="json wf-json">{JSON.stringify(step.output[active], null, 2)}</pre>}
    </div>
  )
}

